import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Shield, Map, FileText, AlertTriangle, Trophy,
  Menu, Sun, Moon, User, LogOut, LayoutDashboard, ChevronRight, Phone, MessageCircle, Brain
} from 'lucide-react';

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem('gtw_theme') === 'dark');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('gtw_theme', dark ? 'dark' : 'light');
  }, [dark]);

  const navLinks = [
    { to: '/', label: 'Home', icon: Shield },
    { to: '/map', label: 'Live Map', icon: Map },
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/smart', label: 'Smart AI', icon: Brain },
    { to: '/blog', label: 'Blog', icon: FileText },
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  const userLinks = [
    { to: '/report/violation', label: 'Report Violation', icon: AlertTriangle },
    { to: '/report/blackspot', label: 'Report Blackspot', icon: AlertTriangle },
    { to: '/my-reports', label: 'My Reports', icon: FileText },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-lg tracking-tight text-foreground">Kalaburagi</span>
              <span className="font-bold text-lg tracking-tight text-primary ml-1">Traffic</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to}>
                <Button
                  variant={isActive(link.to) ? "default" : "ghost"}
                  size="sm"
                  className="gap-2"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Button>
              </Link>
            ))}
            {user && user.role === 'admin' && (
              <Link to="/admin">
                <Button variant={isActive('/admin') ? "default" : "ghost"} size="sm" className="gap-2" data-testid="nav-admin">
                  <LayoutDashboard className="w-4 h-4" />
                  Admin
                </Button>
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {user ? (
              <>
                <div className="hidden md:block">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2" data-testid="user-menu-trigger">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="w-3 h-3 text-primary" />
                        </div>
                        {user.name}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {userLinks.map(link => (
                        <DropdownMenuItem key={link.to} onClick={() => navigate(link.to)} data-testid={`menu-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                          <link.icon className="w-4 h-4 mr-2" />{link.label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2" />Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            ) : (
              <Link to="/login" className="hidden md:block">
                <Button size="sm" data-testid="nav-login">Login</Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <span className="font-bold">Kalaburagi Traffic</span>
                    </div>
                  </div>
                  <nav className="flex-1 p-4 space-y-1">
                    {navLinks.map(link => (
                      <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}>
                        <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive(link.to) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                          <link.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{link.label}</span>
                          <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                        </div>
                      </Link>
                    ))}
                    {user && (
                      <>
                        <div className="pt-4 pb-2 px-3">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Reports</span>
                        </div>
                        {userLinks.map(link => (
                          <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}>
                            <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive(link.to) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                              <link.icon className="w-4 h-4" />
                              <span className="text-sm font-medium">{link.label}</span>
                            </div>
                          </Link>
                        ))}
                        {user.role === 'admin' && (
                          <Link to="/admin" onClick={() => setMobileOpen(false)}>
                            <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive('/admin') ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                              <LayoutDashboard className="w-4 h-4" />
                              <span className="text-sm font-medium">Admin Dashboard</span>
                            </div>
                          </Link>
                        )}
                      </>
                    )}
                  </nav>
                  <div className="p-4 border-t">
                    {user ? (
                      <Button variant="outline" className="w-full gap-2" onClick={() => { logout(); setMobileOpen(false); }} data-testid="mobile-logout">
                        <LogOut className="w-4 h-4" />Logout ({user.name})
                      </Button>
                    ) : (
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full" data-testid="mobile-login">Login / Register</Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">{children}</main>

      {/* Floating Helpline Button (Mobile) */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 md:hidden" data-testid="floating-buttons">
        <a
          href="https://wa.me/919876543210?text=Traffic%20Report%20from%20Kalaburagi"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors"
          data-testid="floating-whatsapp"
        >
          <MessageCircle className="w-5 h-5" />
        </a>
        <a
          href="tel:103"
          className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity"
          data-testid="floating-helpline"
        >
          <Phone className="w-5 h-5" />
        </a>
      </div>

      {/* Footer */}
      <footer className="border-t py-8 px-4 md:px-8" data-testid="main-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-primary" />
                <span className="font-bold">Kalaburagi Traffic</span>
              </div>
              <p className="text-sm text-muted-foreground">Helping make Kalaburagi roads safer for everyone. Report violations, track issues, and contribute to road safety.</p>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-3">Quick Links</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Link to="/report/violation" className="text-muted-foreground hover:text-primary transition-colors">Report Violation</Link>
                <Link to="/report/blackspot" className="text-muted-foreground hover:text-primary transition-colors">Report Blackspot</Link>
                <Link to="/map" className="text-muted-foreground hover:text-primary transition-colors">Live Map</Link>
                <Link to="/dashboard" className="text-muted-foreground hover:text-primary transition-colors">Dashboard</Link>
                <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">Safety Blog</Link>
                <Link to="/leaderboard" className="text-muted-foreground hover:text-primary transition-colors">Leaderboard</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-3">Contact & Social</h4>
              <div className="space-y-2 text-sm">
                <a href="tel:103" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                  <Phone className="w-4 h-4" /> Helpline: 103
                </a>
                <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-muted-foreground hover:text-green-600 transition-colors">
                  <MessageCircle className="w-4 h-4" /> WhatsApp Channel
                </a>
              </div>
            </div>
          </div>
          <div className="border-t pt-4 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>Kalaburagi Traffic &copy; {new Date().getFullYear()} | Kalaburagi District, Karnataka</span>
            <span className="font-medium">A Product of Viral Boostup</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
